import React, { memo } from 'react';
import { Handle, Position, NodeProps, Node } from '@xyflow/react';
import { ActorData } from '../types/diagram.types';

type ActorNodeType = Node<ActorData, 'actor'>;

const ACTOR_TYPE_ICONS: Record<string, string> = {
  client:       '👤',
  lp:           '📈',
  psp:          '💳',
  blockchain:   '🔗',
  notification: '📨',
  compliance:   '🛡️',
};

const ACTOR_TYPE_COLORS: Record<string, string> = {
  client:       '#37474F',
  lp:           '#1565C0',
  psp:          '#6A1B9A',
  blockchain:   '#00695C',
  notification: '#E65100',
  compliance:   '#4E342E',
};

const ActorNode = memo(({ data, selected }: NodeProps<ActorNodeType>) => {
  const color = ACTOR_TYPE_COLORS[data.actorType] ?? '#546E7A';
  const icon  = ACTOR_TYPE_ICONS[data.actorType] ?? '⬛';

  return (
    <div
      style={{
        background: '#FAFAFA',
        border: selected ? `2px solid ${color}` : `1.5px solid ${color}55`,
        borderRadius: 8,
        padding: '8px 12px',
        minWidth: 120,
        textAlign: 'center',
        fontFamily: 'system-ui, sans-serif',
        boxShadow: selected
          ? `0 4px 12px ${color}33`
          : '0 1px 4px rgba(0,0,0,0.08)',
        transition: 'box-shadow 0.15s, border-color 0.15s',
      }}
    >
      <div style={{ fontSize: 20, lineHeight: 1 }}>{icon}</div>
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          color,
          marginTop: 4,
          lineHeight: 1.3,
          maxWidth: 110,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        title={data.name}
      >
        {data.name}
      </div>
      {data.url && (
        <div
          style={{
            fontSize: 9,
            color: '#9E9E9E',
            marginTop: 2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {data.url}
        </div>
      )}

      {/* Horizontal handles (same-zone connections) */}
      <Handle type="source" position={Position.Right} id="right" style={{ background: color, border: '2px solid #fff', width: 9, height: 9 }} />
      <Handle type="target" position={Position.Left}  id="left"  style={{ background: '#90A4AE', border: '2px solid #fff', width: 9, height: 9 }} />

      {/* Vertical handles (cross-zone connections) */}
      {/* TOP: target=top (receive from above), source=top-out (send up, for zone-partners) */}
      <Handle type="target" position={Position.Top} id="top"     style={{ background: '#90A4AE', border: '2px solid #fff', width: 8, height: 8 }} />
      <Handle type="source" position={Position.Top} id="top-out" style={{ background: color, border: '2px solid #fff', width: 8, height: 8, marginLeft: 12 }} />

      {/* BOTTOM: source=bottom (send down, for zone-external), target=bottom-in (receive from below) */}
      <Handle type="source" position={Position.Bottom} id="bottom"    style={{ background: color, border: '2px solid #fff', width: 8, height: 8 }} />
      <Handle type="target" position={Position.Bottom} id="bottom-in" style={{ background: '#90A4AE', border: '2px solid #fff', width: 8, height: 8, marginLeft: 12 }} />
    </div>
  );
});

ActorNode.displayName = 'ActorNode';
export default ActorNode;
